import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconCheckCircle } from '#src/atoms/icons/system/iconCheckCircle.ts';
import { iconX } from '#src/atoms/icons/system/iconX.ts';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { LoadingIndicator } from '#src/atoms/loader/LoadingIndicator/index.tsx';
import { LabelM } from '#src/atoms/typography/index.tsx';
import { TextField } from '#src/components/form/Form/index.tsx';
import { useQuery } from '#src/hooks/graphql/useQuery.ts';
import { glossary } from '#src/lib/glossary.ts';

import { useEnterCode } from '../EnterCodeButton/useEnterCode.ts';
import type {
  EnterCodeInlineQuery,
  EnterCodeInlineQueryVariables,
} from './__generated__/index.graphql.ts';

type ValidationState = 'idle' | 'loading' | 'valid' | 'invalid';

const ENTER_CODE_INLINE_QUERY = gql`
  query EnterCodeInlineQuery {
    cardSet {
      id
      referrerCodeWindowInDays
    }
  }
` as TypedDocumentNode<EnterCodeInlineQuery, EnterCodeInlineQueryVariables>;

const InlineCard = styled(Vertical)`
  max-width: 100%;
  width: 100%;
  padding: var(--intermediate-unit);
  border-radius: var(--double-unit);
  background-color: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

const InlineCardHeader = styled(Horizontal)`
  justify-content: space-between;
  align-items: center;
`;

const InlineTextField = styled(TextField)`
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  background-color: rgba(255, 255, 255, 0.08);

  /* keep the submit button inset from the edge as much as from top/bottom */
  &:has(> button) {
    padding-right: var(--half-unit);
  }

  input {
    color: var(--c-white);
    background-color: transparent;
  }

  input::placeholder {
    opacity: 0.5;
  }

  &.valid input {
    color: var(--c-green-300);
  }
`;

export const EnterCodeInline = () => {
  const { formatMessage } = useIntl();
  const {
    inputValue,
    setInputValue,
    error,
    validateAndApplyCode,
    alreadyApplied,
  } = useEnterCode();
  const [validationState, setValidationState] = useState<ValidationState>(
    alreadyApplied ? 'valid' : 'idle'
  );
  const [inviterName, setInviterName] = useState<string | null>(null);
  const { data } = useQuery(ENTER_CODE_INLINE_QUERY);

  const referrerCodeWindowInDays = data?.cardSet?.referrerCodeWindowInDays;

  const onSubmit = async () => {
    if (!inputValue.trim() || validationState === 'loading') return;
    setValidationState('loading');
    const result = await validateAndApplyCode();
    setInviterName(result.inviterName);
    setValidationState(result.valid ? 'valid' : 'invalid');
  };

  const adornment =
    validationState === 'valid' ? (
      <FontAwesomeIcon icon={iconCheckCircle} color="var(--c-green-300)" />
    ) : validationState === 'loading' ? (
      <LoadingIndicator
        smaller
        style={{
          width: 'unset',
        }}
      />
    ) : inputValue.trim() ? (
      <Button type="button" size="small" color="primary" onClick={onSubmit}>
        <FormattedMessage {...glossary.submit} />
      </Button>
    ) : null;

  return (
    <InlineCard>
      <InlineCardHeader>
        <LabelM bold as="span" color="var(--c-white)">
          <FormattedMessage defaultMessage="Have an invite code?" />
        </LabelM>
      </InlineCardHeader>
      <InlineTextField
        className={validationState === 'valid' ? 'valid' : undefined}
        value={inputValue}
        onChange={e => {
          setValidationState('idle');
          setInviterName(null);
          setInputValue(e.target.value.toUpperCase());
        }}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            onSubmit();
          }
        }}
        name="code"
        placeholder={formatMessage({ defaultMessage: 'Enter your code' })}
        endAdornment={adornment}
        disabled={validationState === 'loading'}
      />
      {validationState === 'valid' ? (
        <LabelM color="var(--c-white)">
          {inviterName ? (
            <FormattedMessage
              defaultMessage="{inviterName} has successfully invited you!"
              values={{ inviterName }}
            />
          ) : (
            <FormattedMessage defaultMessage="Code activated" />
          )}
        </LabelM>
      ) : validationState === 'invalid' && !!error ? (
        <Horizontal gap={0.5}>
          <FontAwesomeIcon icon={iconX} color="var(--c-red-400)" />
          <LabelM color="var(--c-red-400)">{error}</LabelM>
        </Horizontal>
      ) : (
        !!referrerCodeWindowInDays && (
          <LabelM color="var(--c-nd-600)">
            <FormattedMessage
              defaultMessage="You can also enter an invite code in your menu up to {days, plural, one {# day} other {# days}} after starting your journey."
              values={{ days: referrerCodeWindowInDays }}
            />
          </LabelM>
        )
      )}
    </InlineCard>
  );
};
